import { graphql, Link } from 'gatsby';
import React, { useState } from 'react';
import { Column, Container, Row } from '../../components/Grid';
import Layout from '../../components/Layout';
import Section from '../../components/Section';
import Video from '../../components/Video';
import { getKeyedResources } from '../../utils/resource-helper';
import Resource from '../../components/Resource';
import Image from '../../components/Image';

const ClassroomResourcesPage = ({ data }) => {
  const {
    readingComprehensionMasterclass,
    taskCards,
    readingComprehensionBackgroundKnowledge,
    readingComprehensionReadingStrategies,
    readingComprehensionTextStructures,
    readingComprehensionRtuBackgroundKnowledge,
    readingComprehensionRtuReadingStrategies,
    readingComprehensionRtuTextStructures,
  } = getKeyedResources(data);
  const [isTabExpanded, setIsTabExpanded] = useState(false);

  return (
    <Layout
      title="Reading Comprehension"
      className="classroom-resources-subpage classroom-resources-subpage--reading-comprehension"
    >
      <Section>
        <Container>
          <div className="classroom-resources-subpage-header classroom-resources-subpage-header--reading-comprehension">
            <div className="classroom-resources-subpage-header-top-section">
              <Link
                to="/classroom-resources"
                className="classroom-resources-subpage-header-back-button"
              >
                <Image
                  className="classroom-resources-subpage-header-back-icon"
                  filename="back-button-icon.png"
                  alt="Back"
                />
                <span>Back to Learning Resources Overview</span>
              </Link>
              <div className="classroom-resources-subpage-header-resources-tab">
                <button
                  className={`classroom-resources-subpage-header-tab-button ${
                    isTabExpanded ? 'expanded' : ''
                  }`}
                  onClick={() => setIsTabExpanded(!isTabExpanded)}
                >
                  How to use these resources
                  <span className="caret-icon">▼</span>
                </button>
                {isTabExpanded && (
                  <div className="classroom-resources-subpage-header-tab-content">
                    <p>
                      <b>1. Start with the Explainer Video:</b> Watch the
                      Explainer video to understand the key concepts of each big
                      idea of literacy.
                      <br />
                      <b>2. Dive into the Microlearns:</b> Complete the 6–8
                      minute, self-paced modules for each big idea. These
                      modules provide research-based strategies and activities
                      you can immediately use with your students.
                      <br />
                      <b>3. See the Strategies in Action:</b> Watch the masterclass
                      video, where a teacher demonstrates these strategies in a
                      real-world classroom setting.
                      <br />
                      <b>4. Equip Yourself with Task Cards:</b> Print the task
                      cards to keep these strategies at your fingertips and use
                      the printable manipulatives to support hands-on learning
                      with students.
                      <br />
                      <b>5. Apply What You’ve Learned:</b> Use the ready-to-use
                      activity to teach foundational literacy skills like
                      phonics, vocabulary, and comprehension directly in your
                      classroom or after-school program.
                      <br />
                      <b>Why This Approach Works:</b> Each step builds on the
                      previous one, helping you gain foundational knowledge, see
                      effective strategies in practice, and implement them
                      confidently. Whether you’re a classroom teacher or
                      afterschool professional, these resources provide the tools
                      you need to create engaging, impactful literacy experiences
                      for K–2 learners.
                    </p>
                  </div>
                )}
              </div>
            </div>
            <h1 className="classroom-resources-subpage-header-title">
              Reading Comprehension
            </h1>
            <p className="classroom-resources-subpage-header-description">
              Reinforce the foundation of knowledge that students use to
              interpret language with resources that break down the basics of
              reading comprehension.
            </p>
          </div>
        </Container>
      </Section>
      <Section style={{ padding: '0' }}>
        <Container>
          <Link
            to="/classroom-resources"
            className="classroom-resources-subpage-header-back-button classroom-resources-subpage-header-back-button--mobile"
          >
            <Image
              className="classroom-resources-subpage-header-back-icon"
              filename="back-button-icon.png"
              alt="Back"
            />
            <span>Back to Learning Resources Overview</span>
          </Link>
        </Container>
      </Section>
      <Section>
        <Container>
          <div className="classroom-resources-subpage-video">
            <Video guidSrc="79191843-10c6-4023-9b52-087d617c9ff7" />
            <p className="classroom-resources-subpage-video-category">
              Explainer video
            </p>
            <p className="classroom-resources-subpage-video-title">
              What is Reading Comprehension?
            </p>
            <p className="classroom-resources-subpage-video-description">
              Take a closer look at the way our mind extracts meaning from text
              and retains the content more efficiently.
            </p>
          </div>
        </Container>
      </Section>
      <Section className="classroom-resources-subpage-resources classroom-resources-subpage-resources--reading-comprehension">
        <Container>
          <h2 className="classroom-resources-subpage-resources-title">
            Supporting Resources
          </h2>
          <Row>
            <Column size={4}>
              <Resource
                tophat={readingComprehensionBackgroundKnowledge.tophat}
                tophatColor={
                  readingComprehensionBackgroundKnowledge.tophatColor
                }
                title={readingComprehensionBackgroundKnowledge.title}
                duration={readingComprehensionBackgroundKnowledge.duration}
                className={readingComprehensionBackgroundKnowledge.className}
                subtitle={readingComprehensionBackgroundKnowledge.subtitle}
                description={
                  readingComprehensionBackgroundKnowledge.description
                }
                img={readingComprehensionBackgroundKnowledge.img}
                actions={readingComprehensionBackgroundKnowledge.actions}
              />
            </Column>
            <Column size={4}>
              <Resource
                tophat={readingComprehensionReadingStrategies.tophat}
                tophatColor={readingComprehensionReadingStrategies.tophatColor}
                title={readingComprehensionReadingStrategies.title}
                duration={readingComprehensionReadingStrategies.duration}
                className={readingComprehensionReadingStrategies.className}
                subtitle={readingComprehensionReadingStrategies.subtitle}
                description={readingComprehensionReadingStrategies.description}
                img={readingComprehensionReadingStrategies.img}
                actions={readingComprehensionReadingStrategies.actions}
              />
            </Column>
            <Column size={4}>
              <Resource
                tophat={readingComprehensionTextStructures.tophat}
                tophatColor={readingComprehensionTextStructures.tophatColor}
                title={readingComprehensionTextStructures.title}
                duration={readingComprehensionTextStructures.duration}
                className={readingComprehensionTextStructures.className}
                subtitle={readingComprehensionTextStructures.subtitle}
                description={readingComprehensionTextStructures.description}
                img={readingComprehensionTextStructures.img}
                actions={readingComprehensionTextStructures.actions}
              />
            </Column>
          </Row>
          <Row>
            <Column
              className="classroom-resources-subpage-resources-card--horizontal-card"
              size={12}
            >
              <Resource
                horizontal
                tophat={readingComprehensionMasterclass.tophat}
                tophatColor={readingComprehensionMasterclass.tophatColor}
                title={readingComprehensionMasterclass.title}
                duration={readingComprehensionMasterclass.duration}
                className={readingComprehensionMasterclass.className}
                subtitle={readingComprehensionMasterclass.subtitle}
                description={readingComprehensionMasterclass.description}
                img={readingComprehensionMasterclass.img}
                actions={readingComprehensionMasterclass.actions}
              />
            </Column>
          </Row>
          <Row>
            <Column
              className="classroom-resources-subpage-resources-card--horizontal-card task-cards--reading-comprehension"
              size={12}
            >
              <Resource
                horizontal
                tophat={taskCards.tophat}
                tophatColor={taskCards.tophatColor}
                title={taskCards.title}
                duration={taskCards.duration}
                className={taskCards.className}
                subtitle={taskCards.subtitle}
                description={taskCards.description}
                img={taskCards.img}
                actions={taskCards.actions}
              />
            </Column>
          </Row>
          <Row>
            <Column size={4}>
              <Resource
                tophat={readingComprehensionRtuBackgroundKnowledge.tophat}
                tophatColor={
                  readingComprehensionRtuBackgroundKnowledge.tophatColor
                }
                title={readingComprehensionRtuBackgroundKnowledge.title}
                duration={readingComprehensionRtuBackgroundKnowledge.duration}
                className={readingComprehensionRtuBackgroundKnowledge.className}
                subtitle={readingComprehensionRtuBackgroundKnowledge.subtitle}
                description={
                  readingComprehensionRtuBackgroundKnowledge.description
                }
                img={readingComprehensionRtuBackgroundKnowledge.img}
                actions={readingComprehensionRtuBackgroundKnowledge.actions}
              />
            </Column>
            <Column size={4}>
              <Resource
                tophat={readingComprehensionRtuReadingStrategies.tophat}
                tophatColor={
                  readingComprehensionRtuReadingStrategies.tophatColor
                }
                title={readingComprehensionRtuReadingStrategies.title}
                duration={readingComprehensionRtuReadingStrategies.duration}
                className={readingComprehensionRtuReadingStrategies.className}
                subtitle={readingComprehensionRtuReadingStrategies.subtitle}
                description={
                  readingComprehensionRtuReadingStrategies.description
                }
                img={readingComprehensionRtuReadingStrategies.img}
                actions={readingComprehensionRtuReadingStrategies.actions}
              />
            </Column>
            <Column size={4}>
              <Resource
                tophat={readingComprehensionRtuTextStructures.tophat}
                tophatColor={readingComprehensionRtuTextStructures.tophatColor}
                title={readingComprehensionRtuTextStructures.title}
                duration={readingComprehensionRtuTextStructures.duration}
                className={readingComprehensionRtuTextStructures.className}
                subtitle={readingComprehensionRtuTextStructures.subtitle}
                description={readingComprehensionRtuTextStructures.description}
                img={readingComprehensionRtuTextStructures.img}
                actions={readingComprehensionRtuTextStructures.actions}
              />
            </Column>
          </Row>
          <Link
            to="/classroom-resources"
            className="classroom-resources-subpage-header-back-button"
          >
            <Image
              className="classroom-resources-subpage-header-back-icon"
              filename="back-button-icon.png"
              alt="Back"
            />
            <span>Back to Learning Resources Overview</span>
          </Link>
        </Container>
      </Section>
    </Layout>
  );
};

export const query = graphql`
  query ReadingComprehensionResources {
    allResourcesJson(filter: { pages: { in: "classroom-resources" } }) {
      edges {
        node {
          slug
          tophat
          tophatColor
          img
          icon
          subject
          title
          subtitle
          duration
          description
          actions {
            label
            metadata
            module
            moduleName
            link
            download
            noIcon
            asLink
            videoGuid
          }
          horizontal
          className
          pages
        }
      }
    }
  }
`;

export default ClassroomResourcesPage;
